"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { getProductPath } from "@/lib/productPaths";

function SearchImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative w-16 h-16 rounded-lg border border-gray-200 flex-shrink-0 bg-gray-50 shadow-[0_8px_14px_rgba(0,0,0,0.06)] overflow-hidden flex items-center justify-center">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-[#48A018]/25 border-t-[#48A018] rounded-full animate-spin" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
}

export default function HomeSearch({ inline = false, products = [], onSelect }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const containerRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === "") {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const q = value.toLowerCase();
    const filtered = products
      .filter(p => {
        if (typeof p.name === "string" && p.name.toLowerCase().includes(q)) return true;
        if (Array.isArray(p.aliases) && p.aliases.some(a => String(a).toLowerCase().includes(q))) return true;
        return false;
      })
      .slice(0, 5);

    setSuggestions(filtered);
    setIsOpen(filtered.length > 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim() !== "") {
      router.push(`/products/?search=${encodeURIComponent(searchTerm)}`);
      setIsOpen(false);
    }
  };

  const handleSelect = (product) => {
    router.push(getProductPath(product));
    setIsOpen(false);
    if (typeof onSelect === "function") {
      onSelect();
    }
  };

  const formatPrice = (value) => {
    if (typeof value === "string") return value;
    if (typeof value === "number" && !Number.isNaN(value)) {
      return `$${value.toFixed(2)}`;
    }
    return null;
  };

  // Try to surface a representative price for the suggestion
  const getDisplayPrice = (product) => {
    if (!product) return null;
    const direct = formatPrice(product.displayPrice || product.price);
    if (direct) return direct;

    let min = Infinity;
    if (product.options?.forms?.length) {
      product.options.forms.forEach((form) => {
        form.strengths?.forEach((strength) => {
          strength.counts?.forEach((c) => {
            if (typeof c.price === "number" && c.price < min) {
              min = c.price;
            }
          });
        });
      });
    }
    return min !== Infinity ? formatPrice(min) : null;
  };

  return (
    <div
      className={`relative ${inline ? "ml-2 lg:w-[200px] xl:w-[420px]" : "w-full max-w-3xl md:w-[85%] lg:w-[900px] mx-auto"}`}
      ref={containerRef}
    >
      <form onSubmit={handleSubmit} className="flex items-center h-11">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleChange}
          className="flex-1 border border-gray-300 rounded-l-lg px-3 h-11 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#48A018] focus:border-[#48A018] text-[#1f3d1f] placeholder:text-gray-500"
        />
        <button
          type="submit"
          className="bg-[#0a5a3c] text-white rounded-r-lg px-4 h-11 font-semibold hover:bg-[#094c33] transition text-sm shadow-sm"
        >
          Search
        </button>
      </form>

      {/* Dropdown */}
      {isOpen && (
        <ul className="absolute z-50 mt-2 w-full bg-white/92 backdrop-blur-md border border-gray-200 rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.14)] max-h-80 overflow-y-auto p-3 space-y-3">
          {suggestions.map((item, i) => (
            <li
              key={i}
              onClick={() => handleSelect(item)}
              className="group flex items-center gap-4 rounded-xl border border-gray-100 bg-white/95 p-3.5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:border-[#cfe6d5] cursor-pointer"
            >
              <SearchImage src={`/images/products/${item.image}`} alt={item.name} />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-[#1f3d1f] leading-snug">{item.name}</p>
                  {getDisplayPrice(item) && (
                    <span className="whitespace-nowrap rounded-full bg-[#e6f5eb] px-2.5 py-1.5 text-[11px] font-semibold text-[#2f6a36] leading-tight text-center">
                      <span className="text-[10px] tracking-wide block capitalize">As low as</span>
                      <span className="text-[12px] font-bold block">{getDisplayPrice(item)}</span>
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#4a4a4a] leading-snug mt-1">
                  Tap to view details
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
